import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { maskPan } from "../../utils/helpers";
import idIcon from "../../assets/svgIcons/id.svg";
import greenCheckVerified from "../../assets/svgIcons/green-check-verified.svg";

const PARTNER_STUDENT_DETAIL_HIDE_KEYS = new Set([
  "applicantdependents",
  "applicantmaritalstatus",
  "applicantbirthcountry",
  "applicantcitycountry",
  "applicantnationality",
]);

interface StudentDetailsTabProps {
  studentDetails: any[];
  isMobile: boolean;
  pageType?: string;
}

const StudentDetailsTab: React.FC<StudentDetailsTabProps> = ({
  studentDetails,
  isMobile,
  pageType = "DEFAULT",
}) => {
  const isStrictVerifiedFlag = (v: unknown): boolean =>
    v === true || v === "true" || v === 1 || v === "1";

  const {
    fetchUserApplicationData,
    isMobileVerified: stepperIsMobileVerified,
    isEmailVerified: stepperIsEmailVerified,
    horizontalSteps,
    formData: stepperFormData,
  } = useSelector((state: RootState) => state.stepper);
  const app = fetchUserApplicationData as Record<string, unknown> | undefined;
  const form = (stepperFormData || {}) as Record<string, unknown>;
  const mobileVerifiedFromStepperOrApi =
    isStrictVerifiedFlag(stepperIsMobileVerified) ||
    isStrictVerifiedFlag(app?.mobileNoVerifiedFlag) ||
    isStrictVerifiedFlag(form?.mobileNoVerifiedFlag) ||
    isStrictVerifiedFlag(form?.isMobileVerified);
  const emailVerifiedFromStepperOrApi =
    isStrictVerifiedFlag(stepperIsEmailVerified) ||
    isStrictVerifiedFlag(app?.emailAddressVerifiedFlag) ||
    isStrictVerifiedFlag(form?.emailAddressVerifiedFlag) ||
    isStrictVerifiedFlag(form?.isEmailVerified);

  const getStudentDetailsStep = () => {
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => Number(h?.horizontalStepId) === 2
    );

    return applicantLoanStep?.verticalSteps?.find(
      (v: any) =>
        Number(v?.verticalStepId) === 4 &&
        v?.verticalStepName === "Student Details"
    );
  };

  const getFieldValueFromObjects = (fields: any[] = [], key: string) => {
    const field = fields.find(
      (fieldObj: any) =>
        fieldObj &&
        Object.prototype.hasOwnProperty.call(fieldObj, key)
    );

    return field?.[key];
  };

   const normalizeFieldKey = (name: any) =>
    String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  // Saved-value field names (from fetchUserApplicationData) carry a
  // "studentDetails" prefix the config names don't — e.g.
  // studentDetailsSalutation vs salutation — strip it so both shapes
  // normalize to the same key.
  const studentDetailsKeyOf = (field: any) =>
    normalizeFieldKey(field?.name).replace(/^studentdetails?/, "");

  // The fixed display order for Student Details, independent of whatever
  // order the backend's saved-values or field-config APIs happen to return
  // fields in.
  const STUDENT_DETAILS_DISPLAY_ORDER = [
    "salutation",
    "firstname",
    "lastname",
    "phonenumber",
    "email",
    "dob",
    "pan",
    "gender",
    "fatherfirstname",
    "fatherlastname",
    "motherfirstname",
    "motherlastname",
  ];
  const displayOrderIndex = new Map<string, number>(
    STUDENT_DETAILS_DISPLAY_ORDER.map((key, index) => [key, index]),
  );

  // Fields not covered by the fixed order above (e.g. a field added later
  // that isn't one of the standard ones) fall back to the field CONFIG's
  // `fieldOrder` — state.stepper.horizontalSteps names steps via `stepKey`/
  // `stepLabel` (fetchUserApplicationData, the saved-values shape, uses
  // `horizontalStepName`/`verticalStepName` instead — checked too, in case
  // the shape passed in ever matches that instead).
  const studentDetailsFieldOrder = new Map<string, number>();
  const applicantLoanConfigStep = (horizontalSteps || []).find(
    (h: any) =>
      h?.stepKey === "applicant_loan" ||
      h?.stepLabel === "Applicant & Loan Details" ||
      h?.horizontalStepName === "Applicant & Loan Details",
  );
  const studentDetailsConfigStep = applicantLoanConfigStep?.verticalSteps?.find(
    (v: any) =>
      v?.stepLabel === "Student Details" ||
      v?.verticalStepName === "Student Details",
  );
  (studentDetailsConfigStep?.fields || []).forEach((f: any) => {
    if (!f?.name) return;
    studentDetailsFieldOrder.set(normalizeFieldKey(f.name), Number(f.fieldOrder));
  });

  const sortByConfiguredFieldOrder = (fieldsToSort: any[]) => {
    const orderFor = (field: any) => {
      const key = studentDetailsKeyOf(field);
      const fixedIndex = displayOrderIndex.get(key);
      if (fixedIndex !== undefined) return fixedIndex;

      // Anything outside the fixed list sorts after it, ordered by the
      // field config (if available), so it doesn't get sorted mid-list.
      const configOrder = studentDetailsFieldOrder.get(key);
      return STUDENT_DETAILS_DISPLAY_ORDER.length +
        (configOrder === undefined || Number.isNaN(configOrder)
          ? Number.MAX_SAFE_INTEGER
          : configOrder);
    };
    return [...fieldsToSort].sort((a, b) => orderFor(a) - orderFor(b));
  };

  // Helper to map fields from the vertical step to the required format
  function mapStudentDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return [];
    return verticalStep?.fields.map((fieldObj: any) => {
      const name = Object.keys(fieldObj)?.[0];
      let value = fieldObj?.[name];
      // Format DOB as DD-MM-YYYY
      if (
        name.toLowerCase()?.includes("dob") &&
        value &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
      ) {
        const [year, month, day] = value.split("-");
        value = `${day}-${month}-${year}`;
      }
      // Remove 'studentDetails' or 'studentDetail' prefix and format label
      let label = name.replace(/^studentDetails?/i, "");
      // Special case for phone_number
      if (/phone_number/i.test(label)) {
        label = "Phone Number";
      } else {
        // Convert camelCase/snake_case to Title Case
        label = label
          .replace(/_/g, " ")
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (s) => s.toUpperCase())
          .trim();
      }
      return {
        label,
        name,
        value,
      };
    });
  }

  const mergeFieldArrays = (primaryFields: any[] = [], fallbackFields: any[] = []) => {
    const normalizeKey = (field: any) =>
      normalizeFieldKey(field?.name ?? field?.key).replace(/^studentdetails?/, "");

    const primaryMap = new Map<string, any>();
    primaryFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) primaryMap.set(key, field);
    });

    const fallbackMap = new Map<string, any>();
    fallbackFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) fallbackMap.set(key, field);
    });

    const merged = fallbackFields.map((fallbackField) => {
      const key = normalizeKey(fallbackField);
      const primaryField = key ? primaryMap.get(key) : undefined;
      return primaryField
        ? { ...fallbackField, ...primaryField, value: primaryField?.value ?? "" }
        : { ...fallbackField, value: fallbackField?.value ?? "" };
    });

    primaryFields.forEach((primaryField) => {
      const key = normalizeKey(primaryField);
      if (!key || fallbackMap.has(key)) return;
      merged.push({ ...primaryField, value: primaryField?.value ?? "" });
    });

    return merged;
  };

  // Always try to extract student details from Redux if available
  let studentDetailsToShow = studentDetails;

  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    studentDetailsToShow?.length === 0     
  ) {
    studentDetailsToShow = studentDetails || [];
  }
   console.log(fetchUserApplicationData,"fetchUserApplicationData")
  if (
    pageType === "SUMMARY" &&
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    const studentDetailsStep = getStudentDetailsStep();
    const fallbackStudentDetails = studentDetails || [];
    if (studentDetailsStep) {
      const mappedStudentDetails =
        sortByConfiguredFieldOrder(mapStudentDetailsFromVerticalStep(studentDetailsStep));
      studentDetailsToShow = mergeFieldArrays(
        mappedStudentDetails,
        fallbackStudentDetails
      );
    } else {
      studentDetailsToShow = mergeFieldArrays([], fallbackStudentDetails);
    }
  }
	  const getVerificationStatuses = () => {
    // 1. Find the horizontal step
    const eligibilityStep = (fetchUserApplicationData?.horizontalSteps || []).find(
      (h: any) => h.horizontalStepName === "Eligibility Check"
    );

    // 2. Find the specific vertical step by its name
    const targetVerticalStep = (eligibilityStep?.verticalSteps || []).find(
      (v:any) => v.verticalStepName?.trim() === "Verify Yourself and Provide Consent"
    );

    const fields = targetVerticalStep?.fields || [];

    // 3. Find the respective objects containing the keys
    const emailVerifiedObj = fields.find((field: any) => field && 'isEmailVerified' in field);
    const mobileVerifiedObj = fields.find((field: any) => field && 'isMobileVerified' in field);
    console.log(emailVerifiedObj,mobileVerifiedObj,"emailVerifiedObjemailVerifiedObj")
    // 4. Return both values (converting 'true' strings into actual booleans)
	    return {
	      isEmailVerified: emailVerifiedObj ? isStrictVerifiedFlag(emailVerifiedObj.isEmailVerified) : false,
	      isMobileVerified: mobileVerifiedObj ? isStrictVerifiedFlag(mobileVerifiedObj.isMobileVerified) : false
	    };
	  };
  if (!studentDetailsToShow || studentDetailsToShow.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const formatDOB = (value: number | string) => {
    if (!value) return "-";

    const date = new Date(Number(value));
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const studentDetailsStep = getStudentDetailsStep();
  const aadhaarVerifiedFromStep = getFieldValueFromObjects(
    studentDetailsStep?.fields,
    "isAadhaarVerified"
  );
  const aadhaarVerifiedFromRows = studentDetailsToShow?.find(
    (field: any) => field?.name === "isAadhaarVerified"
  )?.value;
  const hasAadhaarVerifiedStatus =
    aadhaarVerifiedFromStep !== undefined ||
    aadhaarVerifiedFromRows !== undefined;
  const isAadhaarVerified = isStrictVerifiedFlag(
    aadhaarVerifiedFromStep ?? aadhaarVerifiedFromRows
  );

  return (
    <>
	      {studentDetailsToShow
        ?.filter(
          (field: any) =>
            !(
              fetchUserApplicationData?.partnerStartedJourney &&
              PARTNER_STUDENT_DETAIL_HIDE_KEYS.has(
                String(field?.name || "")?.toLowerCase()
              )
            )
        )
	        .map((field: any, subIndex: number) => {
	        const isMobileVerifiedField = studentDetailsToShow?.find(
	          (f: any) => String(f?.name || "")?.toLowerCase() === "ismobileverified"
	        );
	        const isEmailVerifiedField = studentDetailsToShow?.find(
	          (f: any) => String(f?.name || "")?.toLowerCase() === "isemailverified"
	        );
        const isMobileVerifiedBase = isStrictVerifiedFlag(
          isMobileVerifiedField?.value
        );
        const isEmailVerifiedBase = isStrictVerifiedFlag(
          isEmailVerifiedField?.value
        );
	        const isPhoneNumber =
	          field?.name === "studentDetailsPhone_number" ||
	          String(field?.name || "")?.toLowerCase() === "phone_number";
	        const isEmail =
	          field?.name?.toLowerCase() === "email" ||
	          field?.name === "Email" ||
	          field?.name === "studentDetailEmail";

        const fieldName = field?.name?.trim()?.toLowerCase();
	        const hasPhoneValue =
	          (fieldName?.includes("studentdetailsphone_number") ||
	            fieldName === "phone_number") &&
	          String(field?.value ?? "")?.trim() !== "";
        const hasEmailValue =
          (fieldName?.includes("studentdetailemail") ||
            fieldName === "email" ||
            fieldName?.endsWith("email")) &&
          String(field?.value ?? "").trim() !== "";

        /** Email row wins if a name could match both (avoid false phone Verified). */
        const treatAsEmailRow =
          isEmail ||
          hasEmailValue ||
          (fieldName?.includes("email") && !fieldName?.includes("verified"));
        const treatAsPhoneRow =
          !treatAsEmailRow &&
          (isPhoneNumber ||
            hasPhoneValue ||
            ((fieldName?.includes("phone") || fieldName?.includes("mobile")) &&
              !fieldName?.includes("verified")));

        const hasDisplayValue =
          String(field?.value ?? "").trim() !== "" &&
          String(field?.value ?? "").trim() !== "-";

	        const isPanVerifiedField = studentDetailsToShow?.find(
	          (f: any) => String(f?.name || "").toLowerCase() === "ispanverified"
	        );
	        const hasPanValue = isStrictVerifiedFlag(isPanVerifiedField?.value);
	        const isPanField = fieldName === "pan";

        const skipNames = [
          "aadhaar",
          "ispanverified",
          "isaadhaarverified",
          "ispan",
          "ismobileverified",
          "isemailverified",
        ];
        if (skipNames?.includes(field?.name?.toLowerCase())) return null;

        // Phone/email: only the matching row uses mobile vs email flags. PAN uses isPanVerified only.
        const panHasDisplay =
          isPanField &&
          String(field.value ?? "")
            .trim()
            .replace(/[\s-]/g, "").length > 0;
  
	        let isVerified = false;
	        let val=getVerificationStatuses();
	        if (treatAsPhoneRow) {
	          isVerified = val?.isMobileVerified || isMobileVerifiedBase || mobileVerifiedFromStepperOrApi;
	        } else if (treatAsEmailRow) {
	          isVerified = val?.isEmailVerified || isEmailVerifiedBase || emailVerifiedFromStepperOrApi;
	        } else if (isPanField) {
	          isVerified = hasPanValue;
	        }
	        const showNotVerified =
	          (treatAsEmailRow || treatAsPhoneRow || isPanField) &&
	          hasDisplayValue &&
	          !isVerified;
        return (
          <Grid item xs={12} sm={6} md={3} key={subIndex}>
            <DetailRow
              item={{
                label: field?.label,
                value: isPanField
                  ? maskPan(field?.value)
                  : fetchUserApplicationData?.partnerStartedJourney &&
                      field?.name === "dob"
                    ? formatDOB(field?.value)
                    : field?.value,
                isVerified,
                showNotVerified,
              }}
            />
          </Grid>
        );
      })}
      {/* {hasAadhaarVerifiedStatus && (
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#eaf8f8",
              borderRadius: "8px",
              px: 2,
              py: 2,
              mt: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img src={idIcon} alt="icon" style={{ width: 20, height: 20 }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#4B465C",
                }}
              >
                Verify Aadhaar Number
              </Typography>
            </Box>

            <Typography
              sx={{
                mt: 1,
                mb: 2,
                fontSize: "12px",
                fontWeight: 400,
                color: "#4B465C",
              }}
            >
              Verify your Aadhaar through Digilocker to keep your application
              going smoothly
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 2,
                py: 0.9,
                borderRadius: "4px",
                border: `1px solid ${isAadhaarVerified ? "#28C76F" : "#FF9F43"}`,
                color: isAadhaarVerified ? "#28C76F" : "#FF9F43",
                backgroundColor: "#ffffff",
              }}
            >
              {isAadhaarVerified && (
                <img
                  src={greenCheckVerified}
                  alt="Verified"
                  style={{ width: 14, height: 14 }}
                />
              )}
              <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                {isAadhaarVerified ? "Verified" : "Not Verified"}
              </Typography>
            </Box>
          </Box>
        </Grid>
      )} */}
    </>
  );
};

export default StudentDetailsTab;
